/**
 * Dashboard principal pour les clients publics - Design mobile-first
 */

import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Chip,
  Button,
  LinearProgress,
  Paper,
  IconButton,
  Card,
  CardContent,
  Fab,
  Alert,
} from '@mui/material';
import {
  Sync,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  HourglassEmpty,
  Settings,
  Notifications as NotificationsIcon,
  Schedule,
  FiberManualRecord,
  People,
} from '@mui/icons-material';
import { RootState } from '../../redux/store';
import { clientDossiersApi, Dossier } from '../../services/clientApi';
import { logout } from '../../redux/slices/clientAuthSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import ReconnectAnefModal from '../../components/client/ReconnectAnefModal';

export default function ClientDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.clientAuth);

  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [anefError, setAnefError] = useState(false);
  const [showReconnectModal, setShowReconnectModal] = useState(false);
  const [stats, setStats] = useState({
    total_dossiers: 0,
    unread_notifications: 0,
    last_sync: null as string | null,
  });
  const lastFetchTimeRef = useRef<number>(0);

  // Détecter si c'est un client cyber (créé par un cyber)
  const isCyberManagedClient = user?.anef_identifiant?.startsWith('cyber_managed_') || false;

  useEffect(() => {
    fetchData();

    // Recharger les données si on revient sur la page après 15 minutes
    const handleFocus = () => {
      const now = Date.now();
      const fifteenMinutes = 15 * 60 * 1000; // 15 minutes en millisecondes

      if (now - lastFetchTimeRef.current > fifteenMinutes) {
        console.log('🔄 Rechargement des données (> 15 min depuis le dernier chargement)');
        fetchData();
      } else {
        console.log('⏭️ Pas de rechargement nécessaire (< 15 min)');
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dossiersRes, statsRes] = await Promise.all([
        clientDossiersApi.getAll(),
        clientDossiersApi.getStats(),
      ]);

      setDossiers(dossiersRes.data.dossiers || []);
      setStats(statsRes.data.stats);
      lastFetchTimeRef.current = Date.now(); // Enregistrer l'heure du chargement
    } catch (error: any) {
      console.error('Erreur chargement données:', error);
      if (error.response?.status === 401) {
        dispatch(logout());
        navigate('/client/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setAnefError(false);
      const response = await clientDossiersApi.syncAll();

      if (response.data.success) {
        const totals = response.data.data.totals || response.data.data;
        const { dossiers = 0, new: newCount = 0, updated = 0, accounts = 1 } = totals;

        alert(
          `✅ Synchronisation terminée !\n\n` +
          `👥 ${accounts} compte(s) synchronisé(s)\n` +
          `📋 ${dossiers} dossier(s) traité(s)\n` +
          `🆕 ${newCount} nouveau(x)\n` +
          `🔄 ${updated} mis à jour`
        );
        // Pas de rechargement - les sockets/polling mettront à jour automatiquement
      }
    } catch (error: any) {
      console.error('Erreur synchronisation:', error);

      // Vérifier si c'est une erreur d'authentification ANEF
      const errorMessage = error.response?.data?.message || error.message || '';
      if (
        errorMessage.toLowerCase().includes('anef') &&
        (errorMessage.toLowerCase().includes('401') ||
         errorMessage.toLowerCase().includes('authentification') ||
         errorMessage.toLowerCase().includes('identifiant'))
      ) {
        setAnefError(true);
        setShowReconnectModal(true);
      } else {
        alert(`❌ Erreur: ${errorMessage}`);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleReconnectSuccess = async () => {
    setAnefError(false);
    setShowReconnectModal(false);
    // Relancer la synchronisation après reconnexion (pas de rechargement après)
    await handleSync();
  };

  // Calculer les notifications non lues (basé sur les événements non lus)
  const notificationsNonLues = dossiers.reduce((count, dossier) => {
    return count + (dossier.unread_events_count || 0);
  }, 0);

  // Compter les dossiers avec demandes de complément non répondues
  // Basé sur anef_demandes_complement sans date_reponse
  const dossiersAvecComplements = dossiers.filter(dossier => {
    if (dossier.anef_demandes_complement && Array.isArray(dossier.anef_demandes_complement)) {
      return dossier.anef_demandes_complement.some((c: any) => !c.date_reponse);
    }
    return false;
  }).length;

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
        <LinearProgress sx={{ width: '100%', maxWidth: 300, mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Chargement de vos données...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 10 }}>
      {/* Header fixe */}
      <Paper
        elevation={2}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderRadius: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
              Bonjour {user?.prenom} {user?.nom} 👋
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Espace de suivi ANEF
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {!isCyberManagedClient && (
              <IconButton onClick={handleSync} disabled={syncing} size="small">
                <Sync className={syncing ? 'rotating' : ''} fontSize="small" />
              </IconButton>
            )}
            <IconButton onClick={() => navigate('/client/settings')} size="small">
              <Settings fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Contenu principal */}
      <Container maxWidth="md" sx={{ mt: 2, px: 2 }}>
        {/* Statistiques compactes */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
          {/* Total dossiers */}
          <Card sx={{ bgcolor: 'primary.50' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {stats.total_dossiers}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Dossiers
              </Typography>
            </CardContent>
          </Card>

          {/* Notifications non lues */}
          <Card sx={{ bgcolor: notificationsNonLues > 0 ? 'warning.50' : 'grey.50' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: notificationsNonLues > 0 ? 'warning.main' : 'text.secondary' }}>
                {notificationsNonLues}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Non lues
              </Typography>
            </CardContent>
          </Card>

          {/* Compléments en attente */}
          <Card sx={{ bgcolor: dossiersAvecComplements > 0 ? 'error.50' : 'grey.50' }}>
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: dossiersAvecComplements > 0 ? 'error.main' : 'text.secondary' }}>
                {dossiersAvecComplements}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                À répondre
              </Typography>
            </CardContent>
          </Card>
        </Box>

        {/* Alerte erreur ANEF */}
        {anefError && (
          <Alert
            severity="error"
            icon={<Warning />}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setShowReconnectModal(true)}
              >
                Reconnecter
              </Button>
            }
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Identifiants ANEF invalides
            </Typography>
            <Typography variant="caption">
              Vos identifiants ANEF ont changé. Cliquez sur "Reconnecter" pour mettre à jour votre mot de passe.
            </Typography>
          </Alert>
        )}

        {/* Informations du gestionnaire cyber */}
        {user?.cyber_manager && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: 'success.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  flexShrink: 0,
                }}
              >
                {user.cyber_manager.prenom.charAt(0)}{user.cyber_manager.nom.charAt(0)}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem', mb: 0.5 }}>
                  Votre gestionnaire
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: '600', color: 'success.dark', mb: 0.5 }}>
                  {user.cyber_manager.prenom} {user.cyber_manager.nom}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    📧 {user.cyber_manager.email}
                  </Typography>
                  {user.cyber_manager.telephone && (
                    <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      📱 {user.cyber_manager.telephone}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Dernière synchronisation */}
        {!isCyberManagedClient && stats.last_sync && (
          <Paper sx={{ p: 1, mb: 2, bgcolor: 'info.50' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Schedule sx={{ fontSize: 14 }} />
              Dernière sync: {new Date(stats.last_sync).toLocaleString('fr-FR', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
          </Paper>
        )}

        {/* Liste des dossiers */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
              📋 Mes dossiers
            </Typography>
            <Chip
              label={`${dossiers.length}`}
              size="small"
              color="primary"
              sx={{ fontWeight: 'bold' }}
            />
          </Box>

          {dossiers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Aucun dossier trouvé
              </Typography>
              {!isCyberManagedClient && (
                <Button
                  startIcon={<Sync />}
                  onClick={handleSync}
                  disabled={syncing}
                  variant="contained"
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  {syncing ? 'Synchronisation...' : 'Synchroniser ANEF'}
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {dossiers.map((dossier) => {
                // Calculer les infos importantes
                const complementsNonRepondus = dossier.anef_demandes_complement?.filter((c: any) => !c.date_reponse) || [];
                const notifNonLues = dossier.anef_notifications?.filter((n: any) => !n.lu && !n.date_lecture) || [];
                const aDesAlerts = complementsNonRepondus.length > 0 || notifNonLues.length > 0;

                return (
                  <Card
                    key={dossier.id}
                    onClick={() => navigate(`/client/dossiers/${dossier.id}`)}
                    sx={{
                      cursor: 'pointer',
                      borderLeft: '4px solid',
                      borderColor: aDesAlerts ? 'warning.main' : 'primary.main',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      {/* En-tête du dossier */}
                      <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: aDesAlerts ? 'warning.main' : dossier.anef_dernier_titre ? 'success.main' : 'info.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {aDesAlerts ? (
                            <Warning sx={{ color: 'white', fontSize: 18 }} />
                          ) : dossier.anef_dernier_titre ? (
                            <CheckCircle sx={{ color: 'white', fontSize: 18 }} />
                          ) : (
                            <HourglassEmpty sx={{ color: 'white', fontSize: 18 }} />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem', lineHeight: 1.2 }}>
                            {dossier.titre || dossier.type_demande || 'Démarche ANEF'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            N° {dossier.numero_demande?.slice(-8) || 'N/A'}
                          </Typography>
                        </Box>
                        {aDesAlerts && (
                          <FiberManualRecord sx={{ color: 'error.main', fontSize: 12 }} />
                        )}
                      </Box>

                      {/* Badges de statut */}
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
                        {dossier.source_type === 'linked' && (
                          <Chip
                            icon={<People sx={{ fontSize: 14 }} />}
                            label={dossier.linked_display_name || 'Compte rattaché'}
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                          />
                        )}
                        <Chip
                          size="small"
                          label={dossier.anef_statut_actuel?.replace(/_/g, ' ').substring(0, 15) || 'En cours'}
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                        {complementsNonRepondus.length > 0 && (
                          <Chip
                            icon={<Warning sx={{ fontSize: 14 }} />}
                            label={`${complementsNonRepondus.length} complément${complementsNonRepondus.length > 1 ? 's' : ''}`}
                            color="warning"
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                          />
                        )}
                        {notifNonLues.length > 0 && (
                          <Chip
                            icon={<NotificationsIcon sx={{ fontSize: 14 }} />}
                            label={`${notifNonLues.length} non lu${notifNonLues.length > 1 ? 'es' : 'e'}`}
                            color="error"
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 'bold' }}
                          />
                        )}
                        {dossier.anef_dernier_titre && !dossier.anef_dernier_titre.toLowerCase().includes('prolongation') && (
                          <Chip
                            icon={<CheckCircle sx={{ fontSize: 14 }} />}
                            label="Titre délivré"
                            color="success"
                            size="small"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>

                      {/* Dates */}
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                          📅 Dépôt: {(dossier.date_depot || dossier.anef_date_depot) ? new Date(dossier.date_depot || dossier.anef_date_depot!).toLocaleDateString('fr-FR') : 'N/A'}
                        </Typography>
                        {dossier.anef_derniere_mise_a_jour && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                            🔄 MAJ: {new Date(dossier.anef_derniere_mise_a_jour).toLocaleDateString('fr-FR')}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Paper>
      </Container>

      {/* Bouton de synchronisation flottant */}
      {!isCyberManagedClient && (
        <Fab
          color="primary"
          variant="extended"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            left: 16,
            mx: 'auto',
            maxWidth: 400,
          }}
          onClick={handleSync}
          disabled={syncing}
        >
          <Sync sx={{ mr: 1 }} className={syncing ? 'rotating' : ''} />
          {syncing ? 'Synchronisation...' : 'Synchroniser ANEF'}
        </Fab>
      )}

      {/* Modal de reconnexion ANEF */}
      <ReconnectAnefModal
        open={showReconnectModal}
        onClose={() => setShowReconnectModal(false)}
        onSuccess={handleReconnectSuccess}
        anefIdentifiant={user?.anef_identifiant}
      />

      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>
    </Box>
  );
}
